import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import type { IStruxTagActivitySessionsQueryVariables } from '~/client/graph/operations/generated/StruxTagActivitySessions.generated'
import StruxTagHistoryStore from '~/client/src/shared/stores/domain/StruxTagHistory.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../localization/LocalizationManager'
import { Loader } from '../Loader'
import { buildSessionGroups } from './grouping'
import SessionDayGroup from './SessionDayGroup'
import StruxTagGpsSummarySheet from './StruxTagGpsSummarySheet'
import { Session } from './types'

interface IProps {
  projectId?: string
  infuseId?: string | null
}

interface IMobXContext {
  projectDateStore?: ProjectDateStore
  struxTagHistoryStore: StruxTagHistoryStore
}

const StruxTagSessionLog: React.FC<IProps> = ({ projectId, infuseId }) => {
  const { projectDateStore, struxTagHistoryStore } = React.useContext(
    MobXProviderContext,
  ) as IMobXContext

  const [selectedSession, setSelectedSession] = React.useState<Session | null>(
    null,
  )
  const [hoveredSessionId, setHoveredSessionId] = React.useState<string | null>(
    null,
  )
  const handleHoverSession = React.useCallback((id: string | null) => {
    setHoveredSessionId(id)
  }, [])
  const handleOpenSession = React.useCallback((session: Session) => {
    setSelectedSession(session)
  }, [])
  const handleCloseSheet = React.useCallback(() => {
    setSelectedSession(null)
  }, [])

  const variables: IStruxTagActivitySessionsQueryVariables = {
    projectId: projectId || '',
    infuseId: infuseId || '',
  }
  const historyState = struxTagHistoryStore.sessionsFor(variables)

  React.useEffect(() => {
    if (!projectId || !infuseId) return
    void struxTagHistoryStore.ensureSessions({ projectId, infuseId })
  }, [infuseId, projectId, struxTagHistoryStore])

  const data = historyState._tag === 'Ready' ? historyState.data : null
  const loading =
    !!projectId &&
    !!infuseId &&
    (historyState._tag === 'NotAsked' || historyState._tag === 'Loading')
  const error = historyState._tag === 'Failed' ? historyState.error : null

  const sessions = React.useMemo(() => {
    return (data?.struxTagActivitySessions || [])
      .filter(Boolean)
      .sort(
        (a, b) => Number(b?.startedAt || 0) - Number(a?.startedAt || 0),
      ) as Session[]
  }, [data])

  const openSession = React.useMemo(
    () => sessions.find(s => !Number(s.endedAt || 0)) || null,
    [sessions],
  )

  const [now, setNow] = React.useState<number>(() => Date.now())
  React.useEffect(() => {
    if (!openSession) return
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [openSession])

  const sessionGroups = React.useMemo(() => {
    if (!projectDateStore) return []
    return buildSessionGroups(sessions, projectDateStore, now)
  }, [projectDateStore, sessions, now])

  const sessionIdsByDay = React.useMemo(() => {
    const map = new Map<number, Set<string>>()
    sessionGroups.forEach(group => {
      map.set(group.dayKey, new Set(group.sessions.map(s => String(s.id))))
    })
    return map
  }, [sessionGroups])

  const openSessionDayKey = React.useMemo(() => {
    if (!openSession || !projectDateStore) return null
    const startedAt = Number(openSession.startedAt || 0)
    if (!startedAt) return null
    return projectDateStore.startOfDay(startedAt).getTime()
  }, [openSession, projectDateStore])

  if (!infuseId) {
    return (
      <div className="tw:text-body-md tw:text-muted-foreground">
        {Localization.translator.noStruxTagLinked}
      </div>
    )
  }

  if (loading) return <Loader />

  if (error) {
    return (
      <div className="tw:text-body-md tw:text-destructive">
        {Localization.translator.somethingWentWrongTryAgainLater}
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="tw:text-body-md tw:text-muted-foreground">
        {Localization.translator.noActivitySessions}
      </div>
    )
  }

  return (
    <>
      <div className="tw:flex tw:flex-col tw:gap-6">
        {sessionGroups.map(group => {
          const idsForDay = sessionIdsByDay.get(group.dayKey)
          const groupHoveredId =
            hoveredSessionId && idsForDay?.has(hoveredSessionId)
              ? hoveredSessionId
              : null
          const groupNow = group.dayKey === openSessionDayKey ? now : null
          return (
            <SessionDayGroup
              key={group.dayKey}
              group={group}
              projectDateStore={projectDateStore}
              hoveredSessionId={groupHoveredId}
              now={groupNow}
              onHoverSession={handleHoverSession}
              onOpenSession={handleOpenSession}
            />
          )
        })}
      </div>
      <StruxTagGpsSummarySheet
        isOpen={!!selectedSession}
        projectId={projectId}
        infuseId={infuseId}
        startedAt={Number(selectedSession?.startedAt || 0)}
        endedAt={Number(selectedSession?.endedAt || 0)}
        onClose={handleCloseSheet}
      />
    </>
  )
}

export default observer(StruxTagSessionLog)
